<script setup lang="ts">
import { Ref, computed, inject, ref } from 'vue';

import { TSButtonBulkAction, TSInputNumber } from 'tsv2-library';
import { MenuOption } from 'tsv2-library/dist/src/components/v2/Menu/Menu.vue';

import { BorrowingTransactionRequest } from '@/types/borrowing.type';
import { SettingTransaction } from '@/types/setting-transaction.type';
import RemoveFromList from './Dialog/RemoveFromList.vue';

const settingTransaction = inject(
  'settingTransaction',
) as Ref<SettingTransaction>;

const selectedData = defineModel<BorrowingTransactionRequest[]>(
  'selectedData',
  {
    required: true,
  },
);

const formData = defineModel<Map<string, BorrowingTransactionRequest>>(
  'formData',
  { required: true },
);

const showRemoveFromList = ref<boolean>(false);

const durationModel = ref<number>(
  settingTransaction.value.borrowingAttrs.defaultBorrowingTime,
);

const bulkOptions = computed<MenuOption[]>(() => {
  const columns: MenuOption[] = [
    {
      label: 'Set Duration',
      icon: 'time-line',
      useAdditionSlot: true,
      visible: true,
    },
    {
      label: 'Remove from List',
      icon: 'delete-bin',
      danger: true,
      visible: true,
      command: (): void => {
        showRemoveFromList.value = true;
      },
    },
  ];

  return columns.filter((col) => col.visible);
});

/**
 * Handle the apply button to set the asset borrow duration on bulkaction.
 */
const handleSetDuration = (): void => {
  selectedData.value.forEach((datum) => {
    const fData = formData.value.get(datum._id);

    if (fData) {
      formData.value.set(datum._id, {
        ...fData,
        duration: durationModel.value,
      });
    }
  });

  selectedData.value = [];
};
</script>
<template>
  <div data-ts-name="borrow-extension-bulkaction">
    <RemoveFromList
      v-model:selected-data="selectedData"
      v-model:visible="showRemoveFromList"
    />
    <TSButtonBulkAction
      v-model:selected-data="selectedData"
      :options="bulkOptions"
      @apply="handleSetDuration"
      naming="Asset(s)"
      table-name="borrow-extension-table"
    >
      <template #addition="{ selectedOption }">
        <template v-if="selectedOption?.label === 'Set Duration'">
          <TSInputNumber
            v-model="durationModel"
            :max="settingTransaction.borrowingAttrs.maxBorrowingTime"
            :min="1"
            :value="durationModel"
            class="!w-10 [&_span]:w-full"
            data-ts-section="set-duration-input"
            placeholder=""
          />
        </template>
      </template>
    </TSButtonBulkAction>
  </div>
</template>
